import {
    put, all, takeLatest,
} from 'redux-saga/effects';
import _ from 'underscore';

import {
    getCookies,
    setCookie,
    getCookie,
} from 'helpers/CookiesHelpers';

import {
    INIT_CMP_APP,
    initCMPSuccess,
    acceptAllCookies,
    hideBanner,
    setAcceptedCookies,
} from '../reduxModules/ConsentManagementPlatform';

// The duration of the consent validity is set to 13 months
export const CONSENT_VALIDITY_DURATION = 13;

function isCurrentUserABot() {
    const botPattern = /bot|Google-Structured-Data-Testing-Tool/gi;
    return botPattern.test(navigator.userAgent);
}

export function* callAction(action) {
    const pathnameFromAction = action.payload.browserPathname;
    let pathnameFromStorage = false;
    let timerFromStorage = false;
    // Usage of try/catch to avoid console errors related to the desactivation of localstorage
    // and/or cookies
    try {
        pathnameFromStorage = window.localStorage.getItem('ypCmpInitialPathname');
        timerFromStorage = window.localStorage.getItem('ypCmpTimer');
    } catch (firstError) {
        // Local storage is disabled/unavailable
        try {
            pathnameFromStorage = getCookie('ypCmpInitialPathname');
            timerFromStorage = getCookie('ypCmpTimer');
        } catch (secondError) {
            // Cookie storage is disabled/unavailable
        }
    }

    if (!pathnameFromStorage) {
        // Usage of try/catch to avoid console errors related to the desactivation of localstorage
        // and/or cookies
        try {
            window.localStorage.setItem('ypCmpInitialPathname', pathnameFromAction);
        } catch (firstError) {
            // Local storage is disabled/unavailable
            try {
                setCookie('ypCmpInitialPathname', pathnameFromAction, CONSENT_VALIDITY_DURATION);
            } catch (secondError) {
                // Cookie storage is disabled/unavailable
            }
        }
        pathnameFromStorage = pathnameFromAction;
    }

    // Handle the reset after 13 months
    const today = Math.floor(Date.now() / 1000);
    if (timerFromStorage && today >= timerFromStorage) {
        if (pathnameFromStorage) {
            // Usage of try/catch to avoid console errors related to the desactivation of
            // localstorage and/or cookies
            try {
                // We need to reset the ypCmpTimer in order to allow banner to appear
                // again after 13 months
                window.localStorage.removeItem('ypCmpTimer');
                // We also need to reset the value of the initial pathname in order to allow
                // the pursuit of navigation behavior
                window.localStorage.setItem('ypCmpInitialPathname', pathnameFromAction);
            } catch (firstError) {
                // Local storage is disabled/unavailable
                try {
                    setCookie('ypCmpTimer', 0, -1);
                    setCookie('ypCmpInitialPathname', pathnameFromAction, CONSENT_VALIDITY_DURATION);
                } catch (secondError) {
                    // Cookie storage is disabled/unavailable
                }
            }
        }
        setCookie('YPConsent', [], -1);
    }

    // Automatically accept all the cookies when a bot is crawling us
    // We "need" Trustpilot cookie to get the json-ld related tags and display
    // the recommandation stars on search engines
    // See https://support.trustpilot.com/hc/en-us/articles/231993108-Walkthrough-Using-Product-Review-TrustBoxes-to-get-rich-snippets-for-SEO
    // And YP-4971 for more information
    if (isCurrentUserABot()) {
        yield all([
            put(acceptAllCookies()),
            put(hideBanner()),
        ]);
        return;
    }
    // Analytics cookies can be activated by default without consent since
    // their configuration is compliant with the CNIL recommandations
    //  We do this at the first ever launch of the app
    if (document.cookie.indexOf('YPConsent') === -1) {
        setCookie('YPConsent', 'GOOGLE_ANALYTICS,HOTJAR', CONSENT_VALIDITY_DURATION);
    }
    const cookies = getCookies();
    let acceptedCookiesFromDocument = [];
    // If  cookies are not readable, getCookies will return an empty object. In this case,
    // we need to hide the banner, and refuse all cookies, which will prevent the execution
    // of all related js scripts
    if (Object.keys(cookies).length === 0) {
        yield all([
            put(hideBanner()),
            put(setAcceptedCookies(acceptedCookiesFromDocument)),
        ]);
    // To decide weither the user has already made a "choice", we use the timerFromStorage variable
    // which is set in 3 cases :
    // 1) accept all from banner,
    // 2) personalized choice,
    // 3) pursuit of navigation.
    } else if (timerFromStorage) {
        let cookieYPConsentAsArray = [];
        // We need to assign a value to cookieYPConsentAsArray only if cookies.YPConsent is defined
        // ie if at least one cookie has been accepted
        if (cookies.YPConsent) {
            cookieYPConsentAsArray = cookies.YPConsent.split(',');
        }
        acceptedCookiesFromDocument = _.map(cookieYPConsentAsArray, (cookie) => ({
            id: cookie,
        }));
        // Initialize app based on previous choices stored in the browser cookies
        yield all([
            put(hideBanner()),
            put(setAcceptedCookies(acceptedCookiesFromDocument)),
        ]);
    }
    yield put(initCMPSuccess());
}

export function* watch() {
    yield takeLatest(INIT_CMP_APP, callAction);
}
